import React from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { PAGE_TYPES } from '@sparefoot/tracking-events';

import { withPageViewTracking } from 'components/core/library/tracking-utils';
import { Page } from 'components/core/Page';

import { HomePageHero } from 'components/landing/HomePageHero';
import { HowItWorksSection } from 'components/brand/HowItWorksSection';
import { StorageTypesSection } from 'components/brand/StorageTypesSection';
import { SizeGuideSection } from 'components/brand/SizeGuideSection';
import { ReviewSection } from 'components/brand/ReviewSection';
import { FeaturedCitiesSection } from 'components/brand/FeaturedCitiesSection';
import { PLPFacilitiesSection } from 'components/plp/PLPFacilitiesSection';
import { FeaturedContent } from 'components/organisms';
import useExperiment from 'hooks/useExperiment';
import heroBackground from 'assets/images/homepage/stuff-self-storage.svg';

import './homepage.scss';

export const enhance = (Component) =>
	withPageViewTracking(PAGE_TYPES.HOME_PAGE)(Component);

function buildBlogItemListSchema(posts) {
	const validPosts = posts.filter(post => post.link && post.content);
	return JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'ItemList',
		'name': 'Featured Storage Blogs',
		'description': 'A curated list of featured blog articles on the homepage',
		'itemListOrder': 'https://schema.org/ItemListOrderAscending',
		'numberOfItems': validPosts.length,
		'itemListElement': validPosts.map((post, i) => ({
			'@type': 'ListItem',
			'position': i + 1,
			'url': post.link,
			'name': post.content,
		})),
	});
}

export function HomePage() {
	const featuredCities = useSelector((state) => state.content.featuredCities);
	const featuredContent = useSelector((state) => state.content.featuredContent);
	const baseUrl = useSelector((state) => state.app.baseUrl);
	const { variation: sf15342Variation } = useExperiment('sf_15342_geolocation_sfdc');
	return (
		<Page
			className="homepage"
			pageType={PAGE_TYPES.HOME}
			landingPage
		>
			<Helmet>
				<link rel="preload" as="image" href={heroBackground} fetchpriority="high" />
				<meta
					name="msvalidate.01"
					content="72430AA401040F456AF99A586F12B284"
				/>
				<link
					rel="canonical"
					href={baseUrl}
				/>
				<script type="application/ld+json">
					{`
						[
							{
								"@context": "https://schema.org",
								"@type": "Organization",
								"@id": "https://www.sparefoot.com/#organization",
								"name": "SpareFoot",
								"legalName": "SpareFoot",
								"description": "SpareFoot is the largest marketplace for storage, making it easier to move and store your stuff. Compare storage options nationwide and find the best solution for your needs, whether it's traditional self-storage or full-service storage.",
								"url": "https://www.sparefoot.com/",
								"logo": {
									"@type": "ImageObject",
									"url": "https://www.sparefoot.com/logo.svg"
								},
								"foundingDate": "2008",
								"founder": {
									"@type": "Person",
									"name": "Chuck Gordon"
								},
								"address": {
									"@type": "PostalAddress",
									"streetAddress": "10900 Research Blvd 160C PMB 3099",
									"addressLocality": "Austin",
									"addressRegion": "TX",
									"postalCode": "78759",
									"addressCountry": "US"
								},
								"areaServed": "US",
								"contactPoint": {
									"@type": "ContactPoint",
									"telephone": "+1-512-705-6208",
									"contactType": "customer service",
									"areaServed": "US",
									"availableLanguage": "English"
								},
								"sameAs": [
									"https://www.facebook.com/SpareFoot/",
									"https://x.com/SpareFoot",
									"https://www.instagram.com/sparefoot/",
									"https://www.pinterest.com/sparefoot/",
									"https://en.wikipedia.org/wiki/SpareFoot",
									"https://www.linkedin.com/company/sparefoot/",
									"https://www.crunchbase.com/organization/sparefoot",
									"https://www.trustpilot.com/review/www.sparefoot.com"
								],
								"aggregateRating": {
									"@type": "AggregateRating",
									"ratingValue": "4.2",
									"bestRating": "5",
									"worstRating": "1",
									"reviewCount": "50270",
									"ratingCount": "50270"
								}
							},
							{
								"@context": "https://schema.org",
								"@type": "WebSite",
								"@id": "https://www.sparefoot.com/#website",
								"name": "SpareFoot",
								"url": "https://www.sparefoot.com/",
								"description": "Find and reserve self-storage, car storage, RV storage, and boat storage near you with SpareFoot -- the nation's largest storage marketplace.",
								"inLanguage": "en-US",
								"publisher": {
									"@id": "https://www.sparefoot.com/#organization"
								},
								"potentialAction": {
									"@type": "SearchAction",
									"target": "https://www.sparefoot.com/search?location={search_term_string}",
									"query-input": "required name=search_term_string"
								}
							}
						]
					`}
				</script>
				{featuredContent && !isEmpty(featuredContent.contentCards) && (
					<script type="application/ld+json">
						{buildBlogItemListSchema(featuredContent.contentCards)}
					</script>
				)}
			</Helmet >
			<HomePageHero />
			{sf15342Variation === 'variation1' && ( // sf_15342_geolocation_sfdc
				<PLPFacilitiesSection />
			)}
			<HowItWorksSection />
			<SizeGuideSection />
			<StorageTypesSection />
			<ReviewSection />
			{featuredContent && !isEmpty(featuredContent.contentCards) && (
				<FeaturedContent
					title="Storage Tips Worth Reading"
					description={featuredContent.description}
					contentCards={featuredContent.contentCards}
					pageType={featuredContent.pageType}
					ctaButton={{ label: 'Get More Moving & Storage Tips', href: 'https://www.sparefoot.com/blog/' }}
					moduleType="blogPosts"
				/>
			)}
			{!isEmpty(featuredCities) && (
				<FeaturedCitiesSection
					cities={featuredCities}
					fillOrder="vertical"
				/>
			)}
		</Page>
	);
}

HomePage.propTypes = {
	featuredCities: PropTypes.arrayOf(PropTypes.object),
	featuredContent: PropTypes.shape({
		title: PropTypes.string,
		description: PropTypes.string,
		pageType: PropTypes.string,
		contentCards: PropTypes.array,
	}),
	baseUrl: PropTypes.string
};

export default enhance(HomePage);
